import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useMaterialMarketStability } from '../../hooks';

const MarketStabilityList = ({ materialCode, data: externalData, loading: externalLoading }) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState(new Set([0])); // First item starts expanded

  // Fetch market stability data from API
  const { data: fetchedMarketStabilityData, loading: fetchedLoading } = useMaterialMarketStability(materialCode, {
    enabled: externalData === undefined
  });
  const marketStabilityData = externalData ?? fetchedMarketStabilityData;
  const loading = externalLoading ?? fetchedLoading;

  // Helper function to get progress bar color based on percentage
  const getProgressBarColor = (percentage) => {
    if (percentage >= 70) return '#4CAF50'; // Green
    if (percentage >= 40) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  // Transform API data to accordion format
  const accordionData = useMemo(() => {
    if (!marketStabilityData || !Array.isArray(marketStabilityData)) {
      return [];
    }

    // Transform API data
    return marketStabilityData
      .filter((item) => item && item.country && Number.isFinite(Number(item.stability)))
      .map((item, index) => ({
      id: index,
      title: item.country,
      percentage: item.stability,
      color: getProgressBarColor(item.stability),
      content: item.stability_description,
      hasProfile: false // No profile links from API data
      }));
  }, [marketStabilityData]);

  const toggleExpanded = (itemId) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(itemId)) {
      newExpandedItems.delete(itemId);
    } else {
      newExpandedItems.add(itemId);
    }
    setExpandedItems(newExpandedItems);
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        padding: '20px',
        textAlign: 'center',
        color: '#CCCCCC',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {t("charts.marketStability.loading")}
      </div>
    );
  }

  if (accordionData.length === 0) {
    return null;
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      backgroundColor: 'transparent',
      borderRadius: '8px',
      padding: '',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {accordionData.map((item) => {
        const isExpanded = expandedItems.has(item.id);
        const progressColor = item.color || getProgressBarColor(item.percentage);
        
        return (
          <div
            key={item.id}
            style={{
              marginBottom: '8px',
              borderRadius: '6px',
              overflow: 'hidden',
              background: 'rgba(51, 51, 51, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3)',
  }}
          >
            {/* Header Section */}
                  <div
                    onClick={() => toggleExpanded(item.id)}
                    style={{
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: isExpanded ? 'transparent' : 'transparent',
                    transition: 'background-color 0.2s ease',
                    gap: '4px'
                    }}
                  >
                    {/* Country Name */}
                    <div style={{
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: '500',
                    minWidth: '80px', // Adjust as needed
                    flexShrink: 0
                    }}>
                    {item.title}
                    </div>

                    {/* Progress Bar Container */}
                    <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                    }}>
                    {/* Progress Bar */}
                    <div style={{
                      flex: 1,
                      height: '6px',
                      backgroundColor: '#555555',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                      height: '100%',
                      width: `${item.percentage}%`,
                      backgroundColor: progressColor,
                      borderRadius: '3px',
                      transition: 'width 0.3s ease'
                      }} />
                    </div>
                    
                    {/* Percentage */}
                    <div style={{
                      color: '#CCCCCC',
                      fontSize: '12px',
                      fontWeight: '500',
                      minWidth: '32px'
                    }}>
                      {item.percentage}%
                    </div>
                    </div>
                    
                    {/* Chevron Icon */}
              <div style={{
                marginLeft: '16px',
                color: '#CCCCCC',
                display: 'flex',
                alignItems: 'center'
              }}>
                {isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </div>
            </div>
            
            {/* Expanded Content */}
            {isExpanded && (
              <div style={{
                padding: '0 16px 16px 16px',
                backgroundColor: 'transparent',
                // borderTop: '1px solid #555555'
              }}>
                <div style={{
                  color: '#CCCCCC',
                  fontSize: '16px',
                  lineHeight: '1.5',
                  marginTop: '12px',
                  marginBottom: item.hasProfile ? '16px' : '0'
                }}>
                  {item.content}
                </div>
                
                {/* Full Profile Link */}
                {item.hasProfile && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}>
                    <button style={{
                      color: '#4CAF50',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 0',
                      textDecoration: 'underline'
                    }}>
                      {t("charts.supplier.fullProfile")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MarketStabilityList;
